import React, { useEffect } from 'react'
import { t } from 'ttag'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { show } from 'redux-modal'
import { updateSelectedFilters } from 'store/actions/filters'
import FilterTitle from 'components/ui/FilterTitle'
import SearchFilter from 'components/ui/SearchFilter'
import SelectedFilter from 'components/ui/SelectedFilter'
import FilterText from 'components/ui/FilterText'
import RemoveIcon from 'components/ui/RemoveIcon'
import SelectedFilterContainer from 'components/ui/SelectedFilterContainer'
import { SELECTED_FILTER_MAX_LINES } from 'store/constants/constants'
import Dotdotdot from 'react-dotdotdot'
import CustomDropDownTreeSelect from 'components/ui/CustomDropdownTreeSelect'
import styled from 'styled-components/macro'

const HelpText = styled.h5`
  margin: 10px 3px !important;
  font-style: italic;
  font-size: 12px;
  color: #4a4a4a;
  font-weight: normal;
`

function ProjectLanguages({
  type,
  filterData,
  values,
  title,
  showFilter,
  customTop,
  position,
  hide,
  selection
}) {
  const filters = useSelector(state => state.filters)
  const { currentSearchable } = filters
  const filterObject = filters.selectedFilters[currentSearchable]

  return (
    <PureProjectLanguages
      type={type}
      values={values}
      filterData={filterData}
      title={title}
      showFilter={showFilter}
      customTop={customTop}
      filterObject={filterObject}
      position={position}
      hide={hide}
      selection={selection}
    />
  )
}

const PureProjectLanguages = React.memo(function PureProjectLanguages({
  type,
  filterData,
  position,
  values,
  title,
  showFilter,
  customTop,
  filterObject,
  hide,
  selection
}) {
  const dispatch = useDispatch()

  const selectedValues = filterObject[values[0]]
  const options = []

  Object.entries(filterData).forEach(([key, value]) => {
    const parentOption = {
      label: value,
      value: key,
      expanded: true
    }
    const selectedOption = selectedValues.filter(s => s.id === key && s.label === value)
    if (selectedOption.length > 0) {
      parentOption.checked = true
    }
    options.push(parentOption)
  })

  useEffect(() => {
    if (showFilter && position != 'reduced') {
      const isProjLangFilter = values[0] === 'project_lang'
      if (isProjLangFilter && filterObject.search.list.length === 0) {
        dispatch(
          show('alertModal', {
            title,
            message: (
              <>
                <br />
                <p>{t`
                Note that this filter has only effect on the text search and the text search seems to be empty.
              `}</p>
              </>
            )
          })
        )
      }
    }
  }, [dispatch, filterObject.search.list.length, showFilter, title, values])

  const onChange = (currentNode, selectedNodes) => {
    const filterValue = selectedNodes.map(node => ({
      id: node.value,
      label: node.label
    }))

    const langs = selectedNodes.map(node => node.value)
    const langData = {
      filterType: type,
      filterId: type,
      filterValue: langs,
      isHigherLevelFilter: true
    }
    dispatch(updateSelectedFilters(langData))

    const data = {
      filterType: type,
      filterId: values[0],
      filterValue,
      isHigherLevelFilter: true
    }

    dispatch(updateSelectedFilters(data))
  }

  function handleFilterRemove(filterId, filterValue) {
    const resultingValue = filterObject[filterId].filter(
      s => s.id !== filterValue.id || s !== filterValue
    )

    const langs = resultingValue.map(node => node.id)
    const langData = {
      filterType: type,
      filterId: type,
      filterValue: langs,
      isHigherLevelFilter: true
    }

    dispatch(updateSelectedFilters(langData))

    const data = {
      filterType: type,
      filterId,
      filterValue: resultingValue,
      isHigherLevelFilter: true
    }
    dispatch(updateSelectedFilters(data))
  }

  function renderSelectedValues() {
    return values.map(value => {
      if (filterObject[value]) {
        const filterValue = filterObject[value]
        if (filterValue instanceof Array) {
          return filterValue.map(v => (
            <SelectedFilterContainer className="selected-filters" key={v.id ? v.id : v}>
              <SelectedFilter>
                <FilterText title={v.label ? v.label : v}>
                  <Dotdotdot clamp={SELECTED_FILTER_MAX_LINES}>{v.label ? v.label : v}</Dotdotdot>
                </FilterText>
                <RemoveIcon
                  className="far fa-times"
                  onClick={() => handleFilterRemove(value, v, true)}
                />
              </SelectedFilter>
            </SelectedFilterContainer>
          ))
        }
      }
      return false
    })
  }
  return (
    <>
      {!selection ? (
        <SearchFilter
          open={showFilter}
          customTop={customTop}
          className={position == 'reduced' ? 'reduced-fl' : ''}
        >
          <div className="sticky-container">
            {title ? <FilterTitle>{title}</FilterTitle> : null}
            {type === 'languages' && (
              <HelpText>
                {t`This function operates better if fields Project name and acronym are unchecked.`}
              </HelpText>
            )}
            <CustomDropDownTreeSelect data={options} onChange={onChange} />
          </div>
        </SearchFilter>
      ) : !hide ? (
        <div className="selected-fl">{renderSelectedValues()}</div>
      ) : null}
    </>
  )
})

/*
function noneSelected() {
  return(
    <SelectedFilterContainer key="1" className="selected-filters">
      <SelectedFilter>
        <FilterText title={'All languages'}>
          <Dotdotdot clamp={SELECTED_FILTER_MAX_LINES}>
            {'All languages'}
          </Dotdotdot>
        </FilterText>
      </SelectedFilter>
    </SelectedFilterContainer>
  )
}
*/
ProjectLanguages.propTypes = {
  type: PropTypes.string,
  filterData: PropTypes.object,
  values: PropTypes.array,
  title: PropTypes.string,
  position: PropTypes.string,
  onClose: PropTypes.func,
  showFilter: PropTypes.bool,
  customTop: PropTypes.string,
  hide: PropTypes.bool,
  selection: PropTypes.bool
}

PureProjectLanguages.propTypes = {
  type: PropTypes.string,
  filterData: PropTypes.object,
  values: PropTypes.array,
  title: PropTypes.string,
  position: PropTypes.string,
  onClose: PropTypes.func,
  showFilter: PropTypes.bool,
  customTop: PropTypes.string,
  filterObject: PropTypes.object,
  hide: PropTypes.bool,
  selection: PropTypes.bool
}

export default ProjectLanguages
